/*********************************************************************
* © Copyright IBM Corp. 2024
*********************************************************************/
import React, {useState} from 'react'
import {AreaHeader, AreaHeaderProps, AreaRow} from './columns/Area'
import {DateHeader, DateHeaderProps, DateRow} from './columns/Date'
import {InfoLevelIcon} from './columns/Level'
import {ConfigurationChangeMessage} from './columns/ConfigurationChangeMessage'
import {UserRow, UserSearchHeaderProps} from './columns/User'
import {ColumnSpec, List} from './List'
import classNames from 'classnames'
import styles from './style/index.css'
import {MessageSearchHeaderProps} from './columns/MessageAudit'
import {ExpandedRowAdditionalComponentContainer} from './ConfigurationAuditList/ExpandedRowAdditionalComponentContainer'
import {getSimpleHeader} from './columns/SimpleHeader'
import intl from './utils/intl/intl'

export interface AuditEvent {
  id: string
  area: string
  userId: string
  eventType: string
  discriminator1?: string
  discriminator2?: string
  discriminator3?: string
  discriminator4?: string
  discriminator5?: string
  createdAt?: Date | undefined
  payload?: any | undefined
}

export interface EnhancedAuditEvent extends AuditEvent {
  userName?: string
  processName?: string
  message?: string
}

export function getListColumnSpec(
  props: ConfigurationAuditListProps,
  isExpanded: (item: AuditEvent) => boolean
): Array<ColumnSpec<AuditEvent>> {
  return [
    {
      id: 'level',
      width: 40,
      HeaderComponent: InfoLevelIcon,
      Component: InfoLevelIcon
    },
    {
      id: 'message',
      HeaderComponent: getSimpleHeader(intl.formatMessage('Message')),
      Component: ({item}) => (
        <ConfigurationChangeMessage item={item} showDetails={isExpanded(item)} />
      ),
      query: props.query,
      onQueryChange: props.onQueryChange
    },
    {
      id: 'user',
      width: 200,
      HeaderComponent: getSimpleHeader(intl.formatMessage('User')),
      Component: UserRow,
      userFilter: props.userId,
      onUserFilterChange: props.onUserIdChange
    },
    {
      id: 'area',
      width: 200,
      HeaderComponent: AreaHeader,
      Component: AreaRow,
      area: props.area,
      onAreaChange: props.onAreaChange
    },
    {
      id: 'date',
      width: 200,
      HeaderComponent: DateHeader,
      Component: DateRow,
      onClearFiltersClick: props.onClearFiltersClick,
      onDateChange: props.onDateChange,
      beforeDate: props.beforeDate,
      afterDate: props.afterDate,
      hasClearButton: props.hasClearButton,
      popoverZIndex: props.popoverZIndex
    }
  ]
}

export type ConfigurationAuditListProps = DateHeaderProps &
  AreaHeaderProps &
  UserSearchHeaderProps &
  MessageSearchHeaderProps & {
    auditEvents: AuditEvent[]
    loadMoreData: (page: number) => void
    hasMoreData: boolean
  }

export const AuditList = (props: ConfigurationAuditListProps) => {
  const [expandedState, setExpandedState] = useState(new Set<string>())
  return (
    <div className={classNames(styles['log'])}>
      <List
        key="audit-list"
        items={props.auditEvents}
        onLoadMore={props.loadMoreData}
        hasMoreData={props.hasMoreData}
        columnSpecs={getListColumnSpec(props, (item: AuditEvent) => expandedState.has(item.id))}
        onRowClick={(item) => {
          if (expandedState.has(item.id)) {
            expandedState.delete(item.id)
          } else {
            expandedState.add(item.id)
          }
          setExpandedState(new Set(expandedState))
        }}
        expandedState={expandedState}
        ExpandedRowComponent={ExpandedRowAdditionalComponentContainer}
      />
    </div>
  )
}
